import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TwoFactorVerification = ({ userId, method, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    // Auto-send email code if method is email
    if (method === 'email' && !emailSent) {
      sendEmailCode();
    }
  }, [method]);

  const sendEmailCode = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/send-email-code`, { user_id: userId });
      setEmailSent(true);
    } catch (err) {
      setError('Kunne ikke sende email kode. Prøv igen.');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get device name
      const getDeviceName = () => {
        const ua = navigator.userAgent;
        let browser = "Unknown Browser";
        let os = "Unknown OS";
        
        // Detect browser
        if (ua.indexOf("Chrome") > -1) browser = "Chrome";
        else if (ua.indexOf("Safari") > -1) browser = "Safari";
        else if (ua.indexOf("Firefox") > -1) browser = "Firefox";
        else if (ua.indexOf("Edge") > -1) browser = "Edge";
        
        // Detect OS
        if (ua.indexOf("Win") > -1) os = "Windows";
        else if (ua.indexOf("Mac") > -1) os = "macOS";
        else if (ua.indexOf("Linux") > -1) os = "Linux";
        else if (ua.indexOf("Android") > -1) os = "Android";
        else if (ua.indexOf("iOS") > -1) os = "iOS";
        
        return `${browser} på ${os}`;
      };
      
      // Verify 2FA code
      const verifyPayload = {
        user_id: userId,
        totp_code: code,
        remember_device: rememberDevice,
        device_name: rememberDevice ? getDeviceName() : null
      };
      
      if (useBackupCode) {
        verifyPayload.backup_code = code;
      }

      // Complete login with 2FA
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login/2fa`,
        verifyPayload,
        { withCredentials: true }  // Important for cookie handling
      );

      // Success - call onSuccess with token and user data
      onSuccess(response.data);
    } catch (err) {
      // Handle different error response formats - ALWAYS convert to string
      let errorMessage = 'Ugyldig kode. Prøv igen.';
      
      try {
        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;
          if (typeof detail === 'string') {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            // Pydantic validation errors
            errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
          } else if (typeof detail === 'object') {
            errorMessage = JSON.stringify(detail);
          }
        } else if (err.response?.data?.message) {
          errorMessage = String(err.response.data.message);
        } else if (err.message) {
          errorMessage = String(err.message);
        }
      } catch (parseError) {
        // If error parsing fails, use default message
        errorMessage = 'Ugyldig kode. Prøv igen.';
      }
      
      // Ensure errorMessage is always a string
      setError(String(errorMessage));
      setCode(''); // Clear code on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          To-Faktor Autentificering
        </h2>
        <p className="text-neutral-600">
          {method === 'email' 
            ? 'Indtast den 6-cifrede kode sendt til din email'
            : useBackupCode
            ? 'Indtast en backup kode'
            : 'Indtast koden fra din autentifikator app'
          }
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {method === 'email' && emailSent && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800">
            ✅ Email kode sendt! Tjek din indbakke.
          </p>
        </div>
      )}

      <form onSubmit={handleVerify}>
        <div className="mb-6">
          <label className="block text-neutral-700 font-medium mb-2">
            {useBackupCode ? 'Backup Kode' : 'Verifikationskode'}
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              const value = e.target.value;
              if (useBackupCode) {
                // Backup code: allow alphanumeric, 8 chars
                setCode(value.toUpperCase().slice(0, 8));
              } else {
                // Regular code: only digits, 6 chars
                setCode(value.replace(/\D/g, '').slice(0, 6));
              }
            }}
            required
            placeholder={useBackupCode ? 'A1B2C3D4' : '123456'}
            maxLength={useBackupCode ? 8 : 6}
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#f4911c] focus:border-transparent text-center text-2xl font-mono tracking-widest"
            autoFocus
          />
        </div>

        {/* Remember device checkbox */}
        <div className="mb-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              className="mt-1 w-4 h-4 text-[#f4911c] border-neutral-300 rounded focus:ring-[#f4911c]"
            />
            <div className="flex-1">
              <span className="text-neutral-700 font-medium">
                Husk denne enhed i 30 dage
              </span>
              <p className="text-xs text-neutral-500 mt-1">
                Du behøver ikke at indtaste 2FA kode på denne enhed i de næste 30 dage
              </p>
            </div>
          </label>
        </div>

        <button
          type="submit"
          disabled={loading || (useBackupCode ? code.length !== 8 : code.length !== 6)}
          className="w-full px-4 py-3 bg-[#f4911c] text-white rounded-lg hover:bg-[#C51A1F] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed mb-4"
        >
          {loading ? 'Verificerer...' : 'Verificer'}
        </button>
      </form>

      {/* Toggle backup code */}
      {method === 'totp' && (
        <div className="text-center mb-4">
          <button
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
              setError('');
            }}
            className="text-sm text-[#f4911c] hover:underline"
          >
            {useBackupCode ? '← Brug autentifikator kode' : 'Brug backup kode'}
          </button>
        </div>
      )}

      {/* Resend email code */}
      {method === 'email' && (
        <div className="text-center mb-4">
          <button
            onClick={sendEmailCode}
            disabled={loading}
            className="text-sm text-[#f4911c] hover:underline"
          >
            Send kode igen
          </button>
        </div>
      )}

      {/* Cancel */}
      {onCancel && (
        <div className="text-center">
          <button
            onClick={onCancel}
            className="text-sm text-neutral-600 hover:text-neutral-800"
          >
            ← Tilbage til login
          </button>
        </div>
      )}
    </div>
  );
};

export default TwoFactorVerification;